import Swal from "sweetalert2";
import { set } from "vue";

// Crear excepciones personalizadas para roles
class RoleInfoException extends Error {
  constructor(message) {
    super(message);
    this.name = "RoleInfoException";
    this.isRoleInfo = true;
  }
}

// Función helper para mostrar alertas según el tipo de excepción
function showErrorAlert(error) {
  if (error.isRoleInfo) {
    Swal.fire({
      icon: "info",
      title: "Información",
      text: error.message,
      confirmButtonText: "OK",
    });
  } else {
    Swal.fire({
      icon: "error",
      title: "Error!",
      text: error.message,
    });
  }
}

function wrapperCatchError(func) {
  try {
    func();
  } catch (error) {
    showErrorAlert(error);
  }
}

function mergePaginatedItems(existingItems, newItems) {
  const existingIds = new Set(existingItems.map((i) => i.id));
  return [...existingItems, ...newItems.filter((i) => !existingIds.has(i.id))];
}

const store = {
  namespaced: true,
  state: {
    boards: [],
    board: {
      id: null,
      title: "",
      description: "",
      isTemplate: false,
      collaborators: [],
      totalCards: 0,
      totalCardsComplete: 0,
      paginatedColumns: {
        items: [
          {
            id: null,
            title: "",
            description: "",
            order: 0,
            color: null,
            total: 0,
            paginatedCards: {
              items: [
                {
                  id: null,
                  title: "",
                  description: "",
                  isDone: false,
                  startDate: null,
                  dueDate: null,
                  effort: 0,
                  order: 0,
                  legoAuditoriaId: null,
                  auditoria: null,
                  legoColumnId: null,
                  createdBy: null,
                  createdAt: null,
                  progreso: 0,
                },
              ],
              total: 0,
              nextCursor: null,
              hasMore: true,
              completed: 0,
            },
          },
        ],
        total: 0,
        nextCursor: null,
        hasMore: true,
        completed: 0,
      },
    },
    card: {},
    currencyRole: {},
    auditoriaOptions: [],
    showAllTasks: false, // Estado para controlar si se muestran todas las tareas
    hasMoreColumns: true, // Indica si hay más columnas por cargar
    loadingColumns: false,
    totalColumns: 0,
    columnsActuales: 0, // Almacena las columnas actuales del tablero
    tasksLoading: {
      columnId: null,
      isLoading: false,
    }, // Estado de carga de tareas por columna
    isLoadingComments: false,
    isLoadingDependents: false,
    isLoadingAttachedFiles: false,
    searcTerm: null,
    isSkeleton: false,
    isDeletingBoard: false, // Estado para indicar si se está eliminando un tablero
    // Estado del zoom
    zoom: {
      level: 1,
      minZoom: 0.5,
      maxZoom: 2,
      zoomStep: 0.1,
      position: "right", // 'right' o 'left'
    },
  },
  mutations: {
    setCardProgress(state, progress) {
      if (state.card) {
        state.card.progreso = progress; // Actualizar el progreso de la tarjeta
      }
    },
    setTasksLoading(state, { columnId, isLoading }) {
      state.tasksLoading[columnId] = isLoading; // Actualizar el estado de carga de tareas para la columna
    },
    addTasksToColumn(state, { columnId, tasks }) {
      const column = state.board.paginatedColumns.items.find(
        (col) => col.id === columnId
      );
      if (column) {
        column.paginatedCards.hasMore = tasks.hasMore;
        column.paginatedCards.nextCursor = tasks.nextCursor;
        //column.paginatedCards.total = tasks.total;
        //column.paginatedCards.completed = tasks.completed;
        column.paginatedCards.items = [
          ...column.paginatedCards.items,
          ...tasks.items,
        ];
      }
    },
    setNextPageDependents(state, paginatedDependents) {
      state.card.paginatedDependents.items = mergePaginatedItems(
        state.card.paginatedDependents.items,
        paginatedDependents.items
      );
      state.card.paginatedDependents.total = paginatedDependents.total;
      state.card.paginatedDependents.nextCursor =
        paginatedDependents.nextCursor;
      state.card.paginatedDependents.hasMore = paginatedDependents.hasMore;
      state.card.paginatedDependents.completed = paginatedDependents.completed;
    },
    setNextPageBoard(state, board) {
      state.board.id = board.id;
      state.board.title = board.title;
      state.board.description = board.description;
      state.board.isTemplate = board.isTemplate;
      state.board.collaborators = board.collaborators;
      state.board.totalCards = board.totalCards;
      state.board.totalCardsComplete = board.totalCardsComplete;

      state.board.paginatedColumns.total = board.columns.total;
      state.board.paginatedColumns.nextCursor = board.columns.nextCursor;
      state.board.paginatedColumns.hasMore = board.columns.hasMore;
      state.board.paginatedColumns.completed = board.columns.completed;

      const incomingItems = (board.columns.items || []).map((col) => ({
        ...col,
        paginatedCards: {
          items: col.paginatedCards?.items || [],
          total: col.paginatedCards?.total || 0,
          nextCursor: col.paginatedCards?.nextCursor || null,
          hasMore: col.paginatedCards?.hasMore ?? true,
          completed: col.paginatedCards?.completed || 0,
        },
      }));

      const hasDummyColumn = state.board.paginatedColumns.items.some(
        (col) => col.id === null
      );

      const hasColumns =
        state.board.paginatedColumns.items.length > 0 && !hasDummyColumn;

      state.board.paginatedColumns.items = hasColumns
        ? mergePaginatedItems(state.board.paginatedColumns.items, incomingItems)
        : incomingItems;

      state.columnsActuales = state.board.paginatedColumns.items.length;
      /**for (let column of state.board.paginatedColumns.items) {
             // Inicializar paginación de tareas para cada columna
             if (!column.paginatedCards) {
             column.paginatedCards = {
             items: column.paginatedCards.items,
             total: column.paginatedCards.total,
             nextCursor: column.paginatedCards.nextCursor,
             hasMore: column.paginatedCards.hasMore,
             completed: column.paginatedCards.completed
             };
             }
             }**/
    },
    setNextPageComments(state, comments) {
      state.card.paginatedComments.items = mergePaginatedItems(
        state.card.paginatedComments.items,
        comments.items
      );
      state.card.paginatedComments.total = comments.total;
      state.card.paginatedComments.nextCursor = comments.nextCursor;
      state.card.paginatedComments.hasMore = comments.hasMore;
    },
    setNextPageAttachedFiles(state, attachedFiles) {
      // Asegurar que state.card existe
      if (!state.card) {
        state.card = {
          id: null,
          title: "",
          assignments: [],
          paginatedAttachedFiles: {
            items: [],
            total: 0,
            nextCursor: null,
            hasMore: false,
          },
        };
      }

      // Asegurar que paginatedAttachedFiles existe
      if (!state.card.paginatedAttachedFiles) {
        state.card.paginatedAttachedFiles = {
          items: [],
          total: 0,
          nextCursor: null,
          hasMore: false,
        };
      }

      // Verificar si attachedFiles tiene datos válidos
      if (!attachedFiles || !attachedFiles.items) {
        return;
      }

      const currentItems = state.card.paginatedAttachedFiles.items || [];
      const newItems = attachedFiles.items || [];

      state.card.paginatedAttachedFiles.items = mergePaginatedItems(
        currentItems,
        newItems
      );
      state.card.paginatedAttachedFiles.total = attachedFiles.total;
      state.card.paginatedAttachedFiles.nextCursor = attachedFiles.nextCursor;
      state.card.paginatedAttachedFiles.hasMore = attachedFiles.hasMore;
    },
    setLoadingAttachedFiles(state, value) {
      state.isLoadingAttachedFiles = value;
    },
    setLoadingColumns(state, value) {
      state.loadingColumns = value;
    },
    setLoadingComments(state, value) {
      state.isLoadingComments = value;
    },
    setLoadingDependents(state, value) {
      state.isLoadingDependents = value;
    },
    setShowAllTasks(state, value) {
      state.showAllTasks = value;
    },
    clearColumns(state) {
      state.board.paginatedColumns.items = [];
      state.board.paginatedColumns.total = 0;
      state.board.paginatedColumns.nextCursor = null;
      state.board.paginatedColumns.hasMore = true;
      state.board.paginatedColumns.completed = 0;
      state.columnsActuales = 0;
    },
    setCurrencyRole(state, role) {
      state.currencyRole = role;
    },
    setAuditoriaOptions(state, options) {
      state.auditoriaOptions = options;
    },
    setBoards(state, boards) {
      state.boards = boards;
    },
    pushBoard(state, board) {
      state.boards.push(board);
    },
    setSkeleton(state, value) {
      state.isSkeleton = value;
    },
    setIsDeletingBoard(state, value) {
      state.isDeletingBoard = value;
    },
    // Mutaciones para el zoom
    setZoomLevel(state, level) {
      state.zoom.level = Math.max(
        state.zoom.minZoom,
        Math.min(level, state.zoom.maxZoom)
      );
    },
    setZoomPosition(state, position) {
      state.zoom.position = position;
    },
    incrementZoom(state) {
      const newLevel = state.zoom.level + state.zoom.zoomStep;
      state.zoom.level = Math.min(newLevel, state.zoom.maxZoom);
    },
    decrementZoom(state) {
      const newLevel = state.zoom.level - state.zoom.zoomStep;
      state.zoom.level = Math.max(newLevel, state.zoom.minZoom);
    },
    resetZoom(state) {
      state.zoom.level = 1;
    },
    toggleZoomPosition(state) {
      state.zoom.position = state.zoom.position === "right" ? "left" : "right";
    },
    setBoard(state) {
      state.board = {
        id: null,
        title: "",
        description: "",
        isTemplate: false,
        collaborators: [],
        totalCards: 0,
        totalCardsComplete: 0,
        paginatedColumns: {
          items: [
            {
              id: null,
              title: "",
              description: "",
              order: 0,
              color: null,
              total: 0,
              paginatedCards: {
                items: [
                  {
                    id: null,
                    title: "",
                    description: "",
                    isDone: false,
                    startDate: null,
                    dueDate: null,
                    effort: 0,
                    order: 0,
                    legoAuditoriaId: null,
                    auditoria: null,
                    legoColumnId: null,
                    createdBy: null,
                    createdAt: null,
                    progreso: 0,
                  },
                ],
                total: 0,
                nextCursor: null,
                hasMore: true,
                completed: 0,
              },
            },
          ],
          total: 0,
          nextCursor: null,
          hasMore: true,
          completed: 0,
        },
      };
    },
    updateCollaboratorsInBoard(state, collaborators) {
      set(state.board, "collaborators", collaborators);
    },
    setColumns(state, columns) {
      set(state.board, "columns", columns);
    },
    setCards(_, { column, cards }) {
      set(column, "cards", cards);
    },
    setCardsByColumnId(state, { columnId, cards }) {
      const column = state.board.paginatedColumns.items.find(
        (c) => c.id === columnId
      );
      column.paginatedCards.items = cards;
    },
    setColumnById(state, { columns }) {
      state.board.paginatedColumns.items = columns;
    },
    setCard(state, card) {
      // Asegurar que los assignments estén inicializados
      if (!card.assignments) {
        card.assignments = [];
      }

      state.card = card;
      // actualizar fecha de subcard localmente
      if (card.isDependent) {
        if (!state.board?.paginatedColumns?.items) return;

        const column = state.board.paginatedColumns.items.find(
          (col) => col.id === card.columnId
        );
        if (!column) return;

        const targetCard = column.paginatedCards?.items?.find(
          (c) => c.id === card.parentId
        );
        if (!targetCard) return;

        const dependent = targetCard.dependents?.find((d) => d.id === card.id);
        if (dependent) {
          dependent.dueDate = card.dueDate;
          dependent.startDate = card.startDate;
        }

        return;
      }
      if (
        Object.keys(card).length &&
        Object.keys(state.board).length &&
        card.legoColumnId &&
        state.board?.paginatedColumns?.items
      ) {
        const column = state.board.paginatedColumns.items.find(
          (c) => c.id === card.legoColumnId
        );
        if (column?.paginatedCards?.items) {
          const index = column.paginatedCards.items.findIndex(
            (c) => c.id === card.id
          );
          if (index !== -1) {
            set(column.paginatedCards.items, index, card);
          }
        }
      }
    },
    setItemInColumn(state, { columnId, obj }) {
      const column = state.board.paginatedColumns.items.find(
        (c) => c.id == columnId
      );
      if (column) {
        for (let key of Object.keys(obj)) {
          set(column, key, obj[key]);
        }
      }
    },
    pushColumn(state, column) {
      const columns = state.board.paginatedColumns.items;
      columns.unshift(column);
      state.board.paginatedColumns.total += 1;
    },
    deleteColumn(state, column) {
      state.board.paginatedColumns.items =
        state.board.paginatedColumns.items.filter((c) => c.id !== column.id);
      state.board.paginatedColumns.total -= 1;
    },
    pushCard(state, card) {
      const column = state.board.paginatedColumns.items.find(
        (c) => c.id === card.legoColumnId
      );
      column.paginatedCards.total += 1;
      let cards = column.paginatedCards.items;
      state.board.totalCards += 1;
      cards.unshift(card);
    },
    pushSubCard(state, subCard) {
      const card = state.card;
      if (!card.paginatedDependents) {
        card.paginatedDependents = {
          items: [],
          total: 0,
          nextCursor: null,
          hasMore: true,
        };
      }
      subCard.assignments = [];
      card.paginatedDependents.items = [
        subCard,
        ...(card.paginatedDependents.items || []),
      ];
      card.paginatedDependents.total =
        (card.paginatedDependents.total || 0) + 1;
    },
    pushChecklist(state, checklist) {
      const card = state.card;
      let checklists = [...card.checklists];
      checklists.push(checklist);
      set(card, "checklists", checklists);
    },
    pushItemInChecklist(state, item) {
      const checklist = state.card.checklists.find(
        (c) => c.id == item.legoChecklistId
      );
      const items = [...checklist.items, item];

      console.log("items", items);

      set(checklist, "items", items);
    },
    pushSubItemInChecklist(state, item) {
      // Encuentra el checklist correspondiente
      const checklist = state.card.checklists.find(
        (c) => c.id == item.legoChecklistId
      );

      if (!checklist) {
        console.error("Checklist no encontrado");
        return;
      }

      // Encuentra el ítem padre (el ítem al que pertenece el subítem)
      const parentItem = checklist.items.find(
        (i) => i.id === item.parentItemId
      );

      if (!parentItem) {
        console.error("Ítem padre no encontrado");
        return;
      }

      // Agrega el subítem a la propiedad `subItems` del ítem padre
      if (!parentItem.subItems) {
        parentItem.subItems = []; // Inicializa `subItems` si no existe
      }

      parentItem.subItems.push(item); // Agrega el subítem

      console.log("Subítem agregado:", item);
      console.log("Ítem padre actualizado:", parentItem);
    },
    pushCommentInCard(state, comment) {
      const card = state.card;
      if (!card.paginatedComments) {
        card.paginatedComments = {
          items: [],
          total: 0,
          nextCursor: null,
          hasMore: true,
        };
      }
      card.paginatedComments.items = [
        comment,
        ...(card.paginatedComments.items || []),
      ];
      card.paginatedComments.total = (card.paginatedComments.total || 0) + 1;
    },
    pushAttachedFileInCard(state, newFile) {
      const card = state.card;
      card.paginatedAttachedFiles.items = [
        newFile,
        ...(card.paginatedAttachedFiles.items || []),
      ];
      card.paginatedAttachedFiles.total =
        (card.paginatedAttachedFiles.total || 0) + 1;
    },
    updateItemInChecklist(state, item) {
      // Encuentra el checklist correspondiente
      const checklist = state.card.checklists.find(
        (c) => c.id == item.legoChecklistId
      );

      if (!checklist) {
        console.error("Checklist no encontrado");
        return;
      }

      // Encuentra el índice del ítem que se va a actualizar
      const index = checklist.items.findIndex((i) => i.id === item.id);

      if (index === -1) {
        console.error("Ítem no encontrado");
        return;
      }

      // Conserva los subítems del ítem actual
      const subItems = checklist.items[index].subItems || [];

      // Actualiza el ítem sin sobrescribir los subítems
      const updatedItem = { ...item, subItems };

      // actualizar el ítem en la lista
      set(checklist.items, index, updatedItem);

      console.log("Ítem actualizado:", updatedItem);
    },
    updateSubItemInChecklist(state, item) {
      // Encuentra el checklist correspondiente
      const checklist = state.card.checklists.find(
        (c) => c.id == item.legoChecklistId
      );

      if (!checklist) {
        console.error("Checklist no encontrado");
        return;
      }

      // Encuentra el ítem padre (el ítem al que pertenece el subítem)
      const parentItem = checklist.items.find(
        (i) => i.id === item.parentItemId
      );

      if (!parentItem) {
        console.error("Ítem padre no encontrado");
        return;
      }

      // Actualiza el subítem dentro de la propiedad `subItems` del ítem padre
      if (parentItem.subItems) {
        parentItem.subItems = parentItem.subItems.map((subItem) =>
          subItem.id === item.id ? item : subItem
        );
      }

      console.log("Subítem actualizado:", item);
      console.log("Ítem padre actualizado:", parentItem);
    },
    updateChecklistItemsOrder(state, reorderChecklistItems) {
      // Recorrer cada checklist
      state.card.checklists.forEach((checklist) => {
        // Crear un mapa para conservar los subítems de cada ítem
        const subItemsMap = new Map();
        checklist.items.forEach((item) => {
          if (item.subItems && item.subItems.length > 0) {
            subItemsMap.set(item.id, item.subItems); // Guardar los subítems
          }
        });

        // Actualizar el orden de los ítems principales
        checklist.items = reorderChecklistItems
          .filter(
            (item) =>
              item.legoChecklistId === checklist.id &&
              item.parentItemId === null
          )
          .sort((a, b) => a.orderCheck - b.orderCheck);

        // Restaurar los subítems en los ítems principales
        checklist.items.forEach((item) => {
          if (subItemsMap.has(item.id)) {
            item.subItems = subItemsMap.get(item.id); // Restaurar los subítems
          }
        });
      });
    },
    renameChecklist(state, data) {
      const index = state.card.checklists.findIndex((c) => c.id == data.id);
      const newChecklist = Object.assign({}, state.card.checklists[index], {
        title: data.title,
      });
      set(state.card.checklists, index, newChecklist);
    },
    setIsDoneCard(state, card) {
      // Actualizar state.card solo si es la misma tarea que se está finalizando
      if (Object.keys(card).length && state.card.id === card.id) {
        set(state.card, "isDone", card.isDone);
        set(state.card, "doneBy", card.doneBy);
        set(state.card, "doneAt", card.doneAt);
      }

      // Buscar en las cards principales
      let allCards = state.board.paginatedColumns.items
        .map((c) => c.paginatedCards.items)
        .flat();

      // Buscar en las subcards de la card activa (paginatedDependents)
      let allSubCards = [];
      if (
        state.card.paginatedDependents &&
        state.card.paginatedDependents.items
      ) {
        allSubCards = state.card.paginatedDependents.items;
      }

      // Unir ambos arrays
      const allPossibleCards = [...allCards, ...allSubCards];

      // Buscar la card a actualizar
      const cardToUpdate = allPossibleCards.find((c) => c && c.id === card.id);
      if (cardToUpdate) {
        var column = state.board.paginatedColumns.items.find(
          (x) => x.id === card.legoColumnId
        );
        column.paginatedCards.completed += card.isDone ? 1 : -1;
        state.board.totalCardsComplete += card.isDone ? 1 : -1;
        cardToUpdate.isDone = card.isDone;
      }
    },
    deleteItemInChecklist(state, item) {
      const checklist = state.card.checklists.find(
        (c) => c.id == item.legoChecklistId
      );
      const items = checklist.items.filter((i) => i.id != item.id);
      set(checklist, "items", items);
    },
    deleteSubItemInChecklist(state, item) {
      // Encuentra el checklist correspondiente
      const checklist = state.card.checklists.find(
        (c) => c.id == item.legoChecklistId
      );

      if (!checklist) {
        console.error("Checklist no encontrado");
        return;
      }

      // Encuentra el ítem padre (el ítem al que pertenece el subítem)
      const parentItem = checklist.items.find(
        (i) => i.id === item.parentItemId
      );

      if (!parentItem) {
        console.error("Ítem padre no encontrado");
        return;
      }

      // Filtra los subítems para eliminar el subítem correspondiente
      if (parentItem.subItems) {
        parentItem.subItems = parentItem.subItems.filter(
          (subItem) => subItem.id !== item.id
        );
      }

      console.log("Subítem eliminado:", item);
      console.log("Ítem padre actualizado:", parentItem);
    },
    deleteChecklist(state, checklist) {
      const card = state.card;
      const checklists = card.checklists.filter((c) => c.id != checklist.id);
      set(card, "checklists", checklists);
    },
    pushAssignmentInCard(state, { assignment, applyToSubCards }) {
      // dar poderes al tablero
      let allCards = state.board.paginatedColumns.items
        .map((c) => c.paginatedCards.items)
        .flat();
      state.card.isDependent
        ? (allCards = [...allCards, state.card])
        : (allCards = [
            ...allCards,
            state.card.paginatedDependents?.items || [],
          ]);

      const cardToUpdate = allCards.find((c) => c.id == assignment.legoCardId);
      if (!cardToUpdate) return;

      // Asegurar que assignments esté inicializado
      if (!Array.isArray(cardToUpdate.assignments)) {
        set(cardToUpdate, "assignments", []);
      }

      // Verificar si ya existe la asignación para evitar duplicados
      const existingAssignment = cardToUpdate.assignments.find(
        (a) => a.collaboratorId === assignment.collaboratorId
      );

      if (!existingAssignment) {
        let assignments = [...cardToUpdate.assignments];
        assignments.push(assignment);
        set(cardToUpdate, "assignments", assignments);

        if (
          Object.keys(state.card).length &&
          state.card.id == assignment.legoCardId
        ) {
          // Asegurar que el state.card.assignments esté inicializado
          if (!Array.isArray(state.card.assignments)) {
            set(state.card, "assignments", []);
          }
          set(state.card, "assignments", assignments);
        }
      }

      // Si applyToSubCards es true y la tarjeta tiene subtareas, agregar a las subtareas
      // Solo procesar si tenemos datos de subtareas disponibles (no en modo lite/Gantt)
      if (
        applyToSubCards &&
        state.card.paginatedDependents?.items?.length > 0
      ) {
        state.card.paginatedDependents.items.forEach((subCard) => {
          // Asegurar que assignments esté inicializado en la subtarea
          if (!Array.isArray(subCard.assignments)) {
            set(subCard, "assignments", []);
          }

          // Verificar si ya existe la asignación en la subtarea para evitar duplicados
          const existingSubAssignment = subCard.assignments.find(
            (a) => a.collaboratorId === assignment.collaboratorId
          );

          if (!existingSubAssignment) {
            const subAssignment = {
              ...assignment,
              legoCardId: subCard.id,
            };
            let subAssignments = [...subCard.assignments];
            subAssignments.push(subAssignment);
            set(subCard, "assignments", subAssignments);
          }
        });
      } else if (applyToSubCards) {
        // En modo lite (Gantt), solo log para debugging
        console.log(
          "Modo lite detectado: No se procesan subtareas localmente, el backend manejará la lógica"
        );
      }
    },
    deleteAssignment(state, { assignment, deleteFromSubCards }) {
      console.log("assignment-deleting ---------->", assignment);
      let allCards = state.board.paginatedColumns.items
        .map((c) => c.paginatedCards.items)
        .flat();
      state.card.isDependent
        ? (allCards = [...allCards, state.card])
        : (allCards = [
            ...allCards,
            state.card.paginatedDependents?.items || [],
          ]);
      const cardToUpdate = allCards.find((c) => c.id == assignment.legoCardId);

      // Asegurar que assignments esté inicializado
      if (!Array.isArray(cardToUpdate.assignments)) {
        set(cardToUpdate, "assignments", []);
        return;
      }

      let assignments = cardToUpdate.assignments.filter(
        (a) => a.id != assignment.id
      );
      set(cardToUpdate, "assignments", assignments);

      if (Object.keys(state.card).length) {
        // Asegurar que el state.card.assignments esté inicializado
        if (!Array.isArray(state.card.assignments)) {
          set(state.card, "assignments", []);
        }
        set(state.card, "assignments", assignments);
      }

      // Si deleteFromSubCards es true y la tarjeta tiene subtareas, eliminar de las subtareas
      // Solo procesar si tenemos datos de subtareas disponibles (no en modo lite/Gantt)
      if (
        deleteFromSubCards &&
        state.card.paginatedDependents?.items?.length > 0
      ) {
        state.card.paginatedDependents.items.forEach((subCard) => {
          // Asegurar que assignments esté inicializado en la subtarea
          if (!Array.isArray(subCard.assignments)) {
            set(subCard, "assignments", []);
            return;
          }

          // Buscar y eliminar la asignación del colaborador en la subtarea
          const subAssignments = subCard.assignments.filter(
            (a) => a.collaboratorId !== assignment.collaboratorId
          );
          set(subCard, "assignments", subAssignments);
        });
      } else if (deleteFromSubCards) {
        // En modo lite (Gantt), solo log para debugging
        console.log(
          "Modo lite detectado: No se procesan subtareas localmente, el backend manejará la lógica"
        );
      }
    },
    setBoardTitle(state, title) {
      set(state.board, "title", title);
    },
    updateSubItemsOrder(state, { parentItemId, reorderSubItems }) {
      state.card.checklists.forEach((checklist) => {
        checklist.items.forEach((item) => {
          if (item.id === parentItemId) {
            item.subItems = reorderSubItems.sort(
              (a, b) => a.orderCheck - b.orderCheck
            );
          }
        });
      });
    },
    deleteCardAndSubCard(state, cardId) {
      // columna de card principal
      const column = state.board.paginatedColumns.items.find((col) =>
        col.paginatedCards.items.some((c) => c.id === cardId)
      );
      if (column) {
        // eliminar card principal
        const card = column.paginatedCards.items.find((c) => c.id === cardId);
        if (card && card.isDone) {
          if (
            typeof column.paginatedCards.completed === "number" &&
            column.paginatedCards.completed > 0
          ) {
            console.log("card eliminada");
            column.paginatedCards.completed -= 1;
            state.board.totalCardsComplete -= 1;
          }
        }
        column.paginatedCards.total -= 1;
        state.board.totalCards -= 1;
        console.log("cards: " + column.paginatedCards);
        if (column.paginatedCards !== undefined) {
          column.paginatedCards.items = column.paginatedCards.items.filter(
            (c) => c.id !== cardId
          );
        }
      } else {
        // eliminar subcard de card principal
        let items = state.card.paginatedDependents.items.filter(
          (c) => c.id !== cardId
        );
        state.card.paginatedDependents.items = items;
        state.card.paginatedDependents.total -= 1;
        state.card.paginatedDependents.nextCursor =
          items.length > 0 ? items[items.length - 1].id : null;
      }
    },
    setSearchOnly(state, { searchTerm }) {
      state.board.paginatedColumns.nextCursor = null;
      state.searcTerm = searchTerm;
    },
  },
  actions: {
    updateCardProgress({ commit }, progress) {
      console.log("updateCardProgress", progress);

      commit("setCardProgress", progress); // Llamar a la mutación para actualizar el progreso
    },
    assertInfoRoleNotOwner({ state }) {
      const roleLevel = state.currencyRole.level;

      if (!roleLevel) {
        throw new RoleInfoException(
          "Danos 5 segundos, la conexión está lenta..."
        );
      }

      if (roleLevel > 1) {
        throw new RoleInfoException(
          "Solo el propietario del tablero puede hacer esta acción."
        );
      }
    },

    assertInfoRoleAuditorOrOwner({ state }) {
      const roleLevel = state.currencyRole.level;

      if (!roleLevel) {
        throw new RoleInfoException(
          "Danos 5 segundos, la conexión está lenta..."
        );
      }

      if (roleLevel > 3) {
        throw new RoleInfoException(
          "Los invitados no pueden hacer modificaciones al tablero."
        );
      }

      if (roleLevel > 2) {
        throw new RoleInfoException(
          "Solo los auditores o el propietario del tablero pueden hacer esta acción."
        );
      }
    },

    assertInfoRoleNotCollaborator({ state, rootGetters }) {
      const roleLevel = state.currencyRole.level;
      const assignments = state.card.assignments.map((a) => a.personaId);
      const userId = rootGetters.userLoggedIn.id;

      if (!roleLevel) {
        throw new RoleInfoException(
          "Danos 5 segundos, la conexión está lenta..."
        );
      }

      if (roleLevel > 3) {
        throw new RoleInfoException(
          "Los invitados no pueden hacer modificaciones al tablero."
        );
      }

      if (roleLevel > 2 && !assignments.includes(userId)) {
        throw new RoleInfoException(
          "No estás asignado a esta tarea. Puedes comunicarte con un auditor o el propietario del tablero."
        );
      }
    },

    assertInfoRoleNotAuditor({ state }) {
      const roleLevel = state.currencyRole.level;

      if (!roleLevel) {
        throw new RoleInfoException(
          "Danos 5 segundos, la conexión está lenta..."
        );
      }

      if (roleLevel > 3) {
        throw new RoleInfoException(
          "Solo los auditores pueden hacer esta acción."
        );
      }
    },

    assertInfoRoleNotCreator({ state, rootGetters }, createdBy) {
      const roleLevel = state.currencyRole.level;
      const userId = rootGetters.userLoggedIn.id;

      if (!roleLevel) {
        throw new RoleInfoException(
          "Danos 5 segundos, la conexión está lenta..."
        );
      }

      if (roleLevel > 3) {
        throw new RoleInfoException(
          "Los invitados no pueden hacer modificaciones al tablero."
        );
      }

      if (roleLevel > 1 && createdBy != userId) {
        throw new RoleInfoException(
          "No eres el creador de esta tarea. puedes comunicarte con el creador o el propietario del tablero."
        );
      }
    },

    async getAndSetBoards({ rootGetters, getters, commit }) {
      const userId = rootGetters.userLoggedIn.id;
      const response = await rootGetters.fetchGet({
        path: `lego/BoardsByUserId/${userId}`,
      });
      if (response.ok) {
        let boards = await response.json();
        //console.log("-------boards---------", boards);
        boards.forEach((board, index) => {
          board.roleInBoard = getters.getRoleInBoard(
            userId,
            board.collaborators
          );
          board.idTour = index + 1; // Asignación incremental (comienza desde 1)
          //console.log("board", board);

          return board;
        });
        commit("setBoards", boards);
      }
    },
    async getAndSetBoardDeep(
      { rootGetters, commit, getters, state },
      {
        boardId,
        personaId,
        loadAllColumns = false,
        searchTerm = null,
        limit = 5,
      }
    ) {
      if (!boardId || !personaId) return;

      const { nextCursor, hasMore } =
        state.board.paginatedColumns === undefined
          ? { nextCursor: null, hasMore: true }
          : state.board.paginatedColumns;

      // Solo salir si no hay más columnas Y no estamos cambiando el filtro de tareas
      if (!hasMore && !searchTerm && loadAllColumns === state.showAllTasks) {
        commit("setSkeleton", false);
        return;
      }

      commit("setLoadingColumns", true);
      // Construir la ruta basada en los parámetros
      let path = `lego/BoardDeepById/${boardId}/${personaId}?loadAllColumns=${loadAllColumns}&limit=${limit}`;

      if (nextCursor) path += `&cursor=${nextCursor}`;
      if (searchTerm) path += `&searchTerm=${encodeURIComponent(searchTerm)}`;

      try {
        const response = await rootGetters.fetchGet({ path });

        if (response.ok) {
          const data = await response.json();
          const board = data;

          if (searchTerm) {
            state.board.paginatedColumns.items = [];
            state.searcTerm = searchTerm;
            commit("setNextPageBoard", data);
          } else {
            board.roleInBoard = getters.getRoleInBoard(
              rootGetters.userLoggedIn.id,
              board.collaborators
            );

            commit("setNextPageBoard", data);
            commit("setCurrencyRole", board.roleInBoard);
          }
        } else {
          const errorText = await response.text();
          new Error("No se pudo recuperar el tablero: " + errorText);
        }
      } catch (error) {
        await Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.message,
        });
      } finally {
        commit("setLoadingColumns", false);
        commit("setSkeleton", false);
      }
    },
    async loadMoreColumns({ rootGetters, state, dispatch }) {
      await dispatch("getAndSetBoardDeep", {
        boardId: state.board.id,
        personaId: rootGetters.userLoggedIn.id,
        loadAllColumns: state.showAllTasks,
        searchTerm: this.state.searchTerm,
      });
    },
    async loadMoreTasksInColumn(
      { rootGetters, commit, state },
      {
        boardId,
        columnId,
        personaId,
        loadAllColumns = state.showAllTasks,
        limit = 1,
        collaboratorRoleLevel = 3,
        nextCursor,
      }
    ) {
      if (!boardId || !columnId || !personaId) return;

      // Iniciar la carga de tareas para la columna específica
      commit("setTasksLoading", { columnId, isLoading: true });

      let path = `lego/LoadMoreTasksInColumn/${boardId}/${columnId}?limit=${limit}&collaboratorRoleLevel=${collaboratorRoleLevel}&personaId=${personaId}&loadAllColumns=${loadAllColumns}`;
      if (nextCursor !== null && nextCursor !== undefined) {
        path += `&nextCursor=${nextCursor}`;
      }

      try {
        const response = await rootGetters.fetchGet({ path });

        console.log("response", response);

        if (response.ok) {
          const data = await response.json();

          commit("addTasksToColumn", { columnId, tasks: data });
        } else {
          throw new Error("No se pudo recuperar las tareas");
        }
      } catch (error) {
        console.log("error", error);

        await Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.message,
        });
      } finally {
        // Finalizar la carga de tareas para la columna específica
        commit("setTasksLoading", { columnId, isLoading: false });
      }
    },
    async loadMoreAttachedFiles({ rootGetters, state, commit }, { cardId }) {
      if (!cardId) {
        return;
      }

      // Verificar si existe el card y su paginatedAttachedFiles
      const paginatedAttachedFiles = state.card?.paginatedAttachedFiles;
      const { nextCursor, hasMore, total } = paginatedAttachedFiles || {
        nextCursor: null,
        hasMore: true,
        total: 0,
      };

      // Si no es la primera carga y no hay más archivos, salir
      if (!hasMore && total !== 0) {
        return;
      }

      // Verificar si ya se está cargando para evitar cargas duplicadas
      if (state.isLoadingAttachedFiles) {
        return;
      }

      try {
        commit("setLoadingAttachedFiles", true);
        let url = `lego/AttachedFilesByCardId/${cardId}?limit=10`;
        if (nextCursor) {
          url += `&cursor=${nextCursor}`;
        }

        const response = await rootGetters.fetchGet({ path: url });
        if (response.ok) {
          const attachedFiles = await response.json();
          commit("setNextPageAttachedFiles", attachedFiles);
        }
      } catch (error) {
        console.error("Error en loadMoreAttachedFiles:", error);
      } finally {
        commit("setLoadingAttachedFiles", false);
      }
    },
    async loadMoreComments({ rootGetters, state, commit }, { cardId }) {
      const { nextCursor, hasMore } = state.card.paginatedComments;
      if (!hasMore) return;

      try {
        commit("setLoadingComments", true);
        let url = `lego/CommentsByCardId/${cardId}?limit=5`;
        if (nextCursor) {
          url += `&cursor=${nextCursor}`;
        }
        const response = await rootGetters.fetchGet({ path: url });
        if (response.ok) {
          const comments = await response.json();
          commit("setNextPageComments", comments);
        }
      } catch (error) {
        console.log("error", error);
      } finally {
        commit("setLoadingComments", false);
      }
    },
    async loadMoreDependents({ rootGetters, state, commit }, { cardId }) {
      const { nextCursor, hasMore } = state.card.paginatedDependents;
      if (!hasMore) return;
      try {
        commit("setLoadingDependents", true);

        let url = `lego/DependentsByCardId/${cardId}?limit=3`;
        if (nextCursor) {
          url += `&cursor=${nextCursor}`;
        }

        const response = await rootGetters.fetchGet({ path: url });
        if (response.ok) {
          const paginatedDependents = await response.json();
          commit("setNextPageDependents", paginatedDependents);
        }
      } catch (error) {
        console.log("error", error);
      } finally {
        commit("setLoadingDependents", false);
      }
    },
    async getAndSetCard({ rootGetters, commit, state }, cardId) {
      try {
        const response = await rootGetters.fetchGet({
          path: `lego/CardById/${cardId}`,
        });
        if (response.ok) {
          let card = await response.json();

          // Buscar la tarjeta en las columnas del tablero para obtener su progreso
          if (state.board?.paginatedColumns?.items) {
            state.board.paginatedColumns.items.some((column) => {
              const existingCard = column.paginatedCards?.items?.find(
                (c) => c.id === cardId
              );
              if (existingCard) {
                card.progreso = existingCard.progreso;
                return true;
              }
              return false;
            });
          }
          // console.log(card.progreso);

          // Preservar los archivos adjuntos existentes solo si son de la misma tarjeta
          const existingCard = state.card;
          const existingAttachedFiles = existingCard?.paginatedAttachedFiles;
          const isSameCard = existingCard && existingCard.id === card.id;

          if (
            isSameCard &&
            existingAttachedFiles &&
            existingAttachedFiles.items &&
            existingAttachedFiles.items.length > 0
          ) {
            card.paginatedAttachedFiles = existingAttachedFiles;
          } else {
            card.paginatedAttachedFiles = {
              items: [],
              total: 0,
              nextCursor: null,
              hasMore: true,
            };
          }

          if (card.legoCardId) card.isDependent = true;

          commit("setCard", card);
        } else {
          throw new Error("No se pudo recuperar la tarjeta");
        }
      } catch (error) {
        await Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.message,
        });
      }
    },

    cleanBoard({ commit }) {
      commit("setBoard");
      commit("setSkeleton", false);
    },
    async deleteBoard({ rootGetters, dispatch, commit }, board) {
      // console.log(board);

      try {
        dispatch("assertInfoRoleNotOwner");

        const result = await Swal.fire({
          title: `¿Desea eliminar el tablero?`,
          text: `El tablero "${board.title}" será eliminado y no podrá recuperarse.`,
          icon: "warning",
          showCancelButton: true,
          cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
          commit("setIsDeletingBoard", true);
          try {
            const response = await rootGetters.fetchDelete({
              path: `Lego/DeleteBoard/${board.id}`,
            });
            if (response.ok) {
              await Swal.fire(
                "Eliminado!",
                "El tablero ha sido eliminado.",
                "success"
              );
              // Retornar true para indicar que la eliminación fue exitosa
              return true;
            } else {
              throw new Error("No se pudo eliminar el tablero");
            }
          } finally {
            commit("setIsDeletingBoard", false);
          }
        }
        // Si se cancela, no hacer nada y el estado se mantiene en false
      } catch (error) {
        commit("setIsDeletingBoard", false);
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.message,
        });
      }
    },
    async crearTablero({ rootGetters, dispatch }, nuevoTablero) {
      try {
        const response = await rootGetters.fetchPost({
          path: "lego/CreateBoard",
          data: nuevoTablero,
        });
        if (response.ok) {
          dispatch("getAndSetBoards");
        } else {
          throw new Error("No se pudo crear el tablero");
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.message,
        });
      }
    },

    setBoardTitle({ rootGetters, commit, state, dispatch }, card) {
      wrapperCatchError(() => {
        if (card.title == state.board.title) return;
        dispatch("assertInfoRoleAuditorOrOwner");

        commit("setBoardTitle", card.title);
        const prevTitle = state.board.title;

        console.log(prevTitle, card.id);

        rootGetters
          .fetchPut({
            path: `Lego/UpdateBoardTitle/${card.id}`,
            data: card,
          })
          .then((response) => {
            if (response.ok) {
              Swal.fire({
                title: "Actualizado",
                text: "Se actualizó el título",
                icon: "success",
                confirmButtonText: "OK",
              });
            } else {
              throw new Error("No se pudo actualizar el título");
            }
          });
      });
    },

    async updateCollaboratorsInBoard(
      { rootGetters, commit },
      { boardId, collaborators }
    ) {
      const response = await rootGetters.fetchPut({
        path: `Lego/UpdateCollaborators/${boardId}`,
        data: collaborators,
      });
      // console.log("collaborators in store (antes del envío)", collaborators);

      // console.log("response", response);

      if (response.ok) {
        const collaboratorsResponse = await response.json();
        // console.log(
        //   "collaboratorsResponse del servidor",
        //   collaboratorsResponse
        // );

        const updatedCollaborators = collaborators.map(
          (colaboradorOriginal) => {
            const colaboradorConId = collaboratorsResponse.find(
              (colaboradorRespuesta) =>
                colaboradorRespuesta.personaId === colaboradorOriginal.personaId
            );
            return colaboradorConId
              ? { ...colaboradorOriginal, id: colaboradorConId.id }
              : colaboradorOriginal;
          }
        );

        // console.log("collaborators con IDs del response", updatedCollaborators);

        commit("updateCollaboratorsInBoard", updatedCollaborators);

        await Swal.fire({
          title: "Actualizado",
          text: "Se actualizaron los colaboradores",
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        await Swal.fire({
          title: "Error!",
          text: "No se actualizaron los colaboradores",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    },
    async renameChecklist({ rootGetters, commit, dispatch }, checklist) {
      try {
        dispatch("assertInfoRoleNotCreator", checklist.createdById);

        // la mejor forma de hacerlo: acción prospectiva
        commit("renameChecklist", {
          id: checklist.id,
          title: checklist.newTitle,
        });

        const response = await rootGetters.fetchPut({
          path: `lego/checklist/${checklist.id}/rename/${checklist.newTitle}`,
        });

        if (!response.ok) {
          commit("renameChecklist", {
            id: checklist.id,
            title: checklist.oldTitle,
          });
          throw new Error("No se pudo renombrar el checklist");
        }
      } catch (error) {
        showErrorAlert(error);
        throw error; // Re-lanzar la excepción para que el componente la capture
      }
    },

    async updateItemInChecklist({ rootGetters, commit, dispatch }, item) {
      try {
        dispatch("assertInfoRoleNotCreator", item.createdById);

        const response = await rootGetters.fetchPut({
          path: `lego/UpdateItemInChecklist/${item.id}`,
          data: item,
        });
        if (response.ok) {
          const newItem = await response.json();
          commit("updateItemInChecklist", newItem);
        } else {
          throw new Error("No se pudo actualizar el item");
        }
      } catch (error) {
        showErrorAlert(error);
        throw error; // Re-lanzar la excepción para que el componente la capture
      }
    },
    async updateSubItemInChecklist({ rootGetters, commit, dispatch }, item) {
      try {
        dispatch("assertInfoRoleNotCreator", item.createdById);

        const response = await rootGetters.fetchPut({
          path: `lego/UpdateItemInChecklist/${item.id}`,
          data: item,
        });
        if (response.ok) {
          const newItem = await response.json();
          commit("updateSubItemInChecklist", newItem);
        } else {
          throw new Error("No se pudo actualizar el subitem");
        }
      } catch (error) {
        showErrorAlert(error);
        throw error; // Re-lanzar la excepción para que el componente la capture
      }
    },
    async SetIsDoneItemInChecklist({ rootGetters, commit, dispatch }, item) {
      try {
        dispatch("assertInfoRoleNotCollaborator");
        const response = await rootGetters.fetchPut({
          path: `lego/SetIsDoneItemInChecklist/${item.id}`,
          data: item,
        });
        if (response.ok) {
          const newItem = await response.json();
          commit("updateItemInChecklist", newItem);
        } else {
          throw new Error("No se pudo actualizar el item");
        }
      } catch (error) {
        showErrorAlert(error);
      }
    },
    async SetIsDoneSubItemInChecklist({ rootGetters, commit, dispatch }, item) {
      try {
        dispatch("assertInfoRoleNotCollaborator");
        const response = await rootGetters.fetchPut({
          path: `lego/SetIsDoneItemInChecklist/${item.id}`,
          data: item,
        });
        if (response.ok) {
          const newItem = await response.json();
          commit("updateSubItemInChecklist", newItem);
        } else {
          throw new Error("No se pudo actualizar el item");
        }
      } catch (error) {
        showErrorAlert(error);
      }
    },

    async setIsDoneCard({ rootGetters, commit }, card) {
      try {
        const response = await rootGetters.fetchPut({
          path: `lego/SetIsDoneCard/${card.id}`,
          data: card,
        });
        if (response.ok) {
          const newCard = await response.json();
          commit("setIsDoneCard", newCard);
        } else {
          new Error("No se pudo actualizar la tarjeta");
        }
      } catch (error) {
        await Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.message,
        });
      }
    },

    async setValidationCard({ rootGetters, commit }, card) {
      try {
        const response = await rootGetters.fetchPut({
          path: `lego/SetValidationCard/${card.id}`,
          data: card,
        });
        if (response.ok) {
          const newCard = await response.json();
          console.log(commit, newCard);
          // commit('setIsDoneCard', newCard)
        } else {
          throw new Error("No se pudo validar la tarea");
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.message,
        });
      }
    },

    async addColumn({ rootGetters, commit }, column) {
      try {
        const response = await rootGetters.fetchPost({
          path: "lego/CreateColumn",
          data: column,
        });
        if (response.ok) {
          const newColumn = await response.json();
          commit("pushColumn", newColumn);
        } else {
          throw new Error("No se pudo crear la columna");
        }
      } catch (error) {
        await Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.message,
        });
      }
    },

    async addCard({ rootGetters, commit }, card) {
      try {
        const response = await rootGetters.fetchPost({
          path: "lego/CreateCard",
          data: card,
        });
        if (response.ok) {
          const newCard = await response.json();
          commit("pushCard", newCard);
        } else {
          throw new Error("No se pudo crear la tarjeta");
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.message,
        });
      }
    },

    async addSubCard({ rootGetters, commit, dispatch }, subCard) {
      try {
        dispatch("assertInfoRoleNotCollaborator");
        const response = await rootGetters.fetchPost({
          path: "lego/CreateCard",
          data: subCard,
        });

        if (response.ok) {
          const newCard = await response.json();
          commit("pushSubCard", newCard);
        } else {
          throw new Error("No se pudo crear la tarjeta");
        }
      } catch (error) {
        showErrorAlert(error);
      }
    },

    async addChecklist({ rootGetters, commit, dispatch }, checklist) {
      try {
        dispatch("assertInfoRoleNotCollaborator");
        const response = await rootGetters.fetchPost({
          path: "lego/CreateChecklist",
          data: checklist,
        });
        if (response.ok) {
          const newChecklist = await response.json();
          newChecklist.items = [];
          commit("pushChecklist", newChecklist);
        } else {
          throw new Error("No se pudo crear el checklist");
        }
      } catch (error) {
        showErrorAlert(error);
      }
    },

    async addItemInChecklist({ rootGetters, commit, dispatch }, item) {
      try {
        dispatch("assertInfoRoleNotCollaborator");
        const response = await rootGetters.fetchPost({
          path: "lego/CreateChecklistItem",
          data: item,
        });
        if (response.ok) {
          const newItem = await response.json();
          commit("pushItemInChecklist", newItem);
        } else {
          throw new Error("No se pudo crear el item");
        }
      } catch (error) {
        showErrorAlert(error);
      }
    },
    async addSubItemInChecklist({ rootGetters, commit, dispatch }, item) {
      try {
        dispatch("assertInfoRoleNotCollaborator");
        const response = await rootGetters.fetchPost({
          path: "lego/CreateChecklistSubItem",
          data: item,
        });
        if (response.ok) {
          const newItem = await response.json();
          commit("pushSubItemInChecklist", newItem);
        } else {
          throw new Error("No se pudo crear el item");
        }
      } catch (error) {
        showErrorAlert(error);
      }
    },
    async reorderSubItemsInChecklist(
      { commit, rootGetters, dispatch },
      { parentItemId, reorderSubItems }
    ) {
      try {
        dispatch("assertInfoRoleNotCollaborator");
        const response = await rootGetters.fetchPost({
          path: "lego/ReorderSubItemsInChecklist",
          data: reorderSubItems,
        });
        if (response.ok) {
          commit("updateSubItemsOrder", { parentItemId, reorderSubItems });
        } else {
          throw new Error("No se pudo reordenar los subítems");
        }
      } catch (error) {
        showErrorAlert(error);
      }
    },
    async reorderChecklistItems(
      { commit, rootGetters, dispatch },
      reorderChecklistItems
    ) {
      console.log("reorderItems", reorderChecklistItems);

      try {
        dispatch("assertInfoRoleNotCollaborator");
        const response = await rootGetters.fetchPost({
          path: "lego/ReorderChecklistItems",
          data: reorderChecklistItems,
        });
        if (response.ok) {
          console.log("response", response);

          // Actualizar el estado local
          commit("updateChecklistItemsOrder", reorderChecklistItems);
        } else {
          throw new Error("No se pudo reordenar el item");
        }
      } catch (error) {
        showErrorAlert(error);
      }
    },

    async addCommentInCard({ rootGetters, commit, dispatch }, comment) {
      try {
        dispatch("assertInfoRoleNotCollaborator");

        const response = await rootGetters.fetchPost({
          path: "lego/CreateComment",
          data: comment,
        });
        if (response.ok) {
          const newComment = await response.json();
          commit("pushCommentInCard", newComment);
        } else {
          throw new Error("No se pudo crear el comentario");
        }
      } catch (error) {
        showErrorAlert(error);
      }
    },

    async addAssignment({ rootGetters, commit }, assignment) {
      console.log("assignment", assignment);

      try {
        const { applyToSubCards, ...assignmentData } = assignment;
        const queryParams = applyToSubCards
          ? `?applyToSubCards=${applyToSubCards}`
          : "";

        const response = await rootGetters.fetchPost({
          path: `lego/AssignmentResponsibility${queryParams}`,
          data: assignmentData,
        });
        if (response.ok) {
          const newAssignment = await response.json();
          commit("pushAssignmentInCard", {
            assignment: newAssignment,
            applyToSubCards: applyToSubCards,
          });
        } else {
          throw new Error("No se pudo crear la asignación");
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.message,
        });
      }
    },

    async addAttachedFile({ rootGetters, commit, dispatch }, file) {
      try {
        dispatch("assertInfoRoleNotCollaborator");
        const response = await rootGetters.fetchPost({
          path: "lego/CreateAttachedFileInCard",
          data: file,
        });
        if (response.ok) {
          const resultFile = await response.json();
          commit("pushAttachedFileInCard", resultFile);
        } else {
          throw new Error("No se pudo crear el archivo adjunto");
        }
      } catch (error) {
        showErrorAlert(error);
      }
    },

    setItemInColumn(
      { rootGetters, commit, state, dispatch },
      { columnId, obj }
    ) {
      wrapperCatchError(() => {
        dispatch("assertInfoRoleAuditorOrOwner");

        const column = state.board.paginatedColumns.items.find(
          (c) => c.id == columnId
        );

        if (column) {
          commit("setItemInColumn", { columnId, obj });

          Object.assign(column, obj);
          rootGetters.fetchPut({
            path: `Lego/UpdateColumn/${columnId}`,
            data: column,
          });
        }
      });
    },

    setItemInCard({ rootGetters, commit, state, dispatch }, obj) {
      wrapperCatchError(() => {
        dispatch("assertInfoRoleNotCollaborator");

        const card = { ...state.card };
        Object.assign(card, obj);
        commit("setCard", card);

        rootGetters.fetchPut({
          path: `Lego/UpdateCard/${card.id}`,
          data: card,
        });
      });
    },
    // eslint-disable-next-line no-unused-vars
    async setAsyncItemInCard({ rootGetters, commit, state }, card) {
      console.log("setAsyncItemInCard", card.isDependent);

      const userId = rootGetters.userLoggedIn.id;
      try {
        const response = await rootGetters.fetchPut({
          path: `Lego/UpdateCard/${card.id}/${userId}`,
          data: card,
        });
        if (response.ok) {
          commit("setCard", card);
        } else {
          throw new Error("No se pudo actualizar la propiedad");
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.message,
        });
      }
    },
    setColumns({ rootGetters, commit, dispatch }, columns) {
      wrapperCatchError(() => {
        dispatch("assertInfoRoleAuditorOrOwner");

        const promises = columns.map((column, index) => {
          column.order = index;
          return rootGetters.fetchPut({
            path: `Lego/UpdateColumn/${column.id}`,
            data: column,
          });
        });
        Promise.all(promises).then(() => {
          console.log("column move success");
          commit("setColumns", columns);
        });
      });
    },
    async moveColumn({ rootGetters, commit, state }, { columnId, newIndex }) {
      const column = state.board.paginatedColumns.items.find(
        (c) => c.id === columnId
      );
      if (!column) return;

      const columns = [...state.board.paginatedColumns.items];

      const oldIndex = columns.findIndex((c) => c.id === columnId);
      if (oldIndex === -1) return;

      const [movedCard] = columns.splice(oldIndex, 1);
      columns.splice(newIndex, 0, movedCard);

      commit("setColumnById", { columns: columns });

      // Llamada real al backend
      try {
        await rootGetters.fetchPut({
          path: `Lego/OrderColumn/${columnId}?newIndex=${newIndex}`,
        });
      } catch (e) {
        // Revertir en caso de error
        commit("setColumnById", { columns: columns });
        await Swal.fire({
          icon: "error",
          title: "Error!",
          text: "No se pudo mover la tarjeta",
        });
      }
    },

    /**async moveCardv3({state, rootGetters, commit}, {cardId, sourceColumnId, targetColumnId, newIndex}) {
            const sourceColumn = state.board.paginatedColumns.items.find(c => c.id === sourceColumnId);
            const targetColumn = state.board.paginatedColumns.items.find(c => c.id === targetColumnId);
            if (!sourceColumn || !targetColumn) return;

            const prevSourceCards = [...sourceColumn.paginatedCards.items];
            const prevTargetCards = [...targetColumn.paginatedCards.items];

            // Movimiento dentro de la misma columna
            if (sourceColumnId === targetColumnId) {
                const cards = [...prevSourceCards];
                const oldIndex = cards.findIndex(c => c.id === cardId);
                if (oldIndex === -1) return;

                const [movedCard] = cards.splice(oldIndex, 1);
                cards.splice(newIndex, 0, movedCard);

                commit("setCardsByColumnId", {
                    columnId: sourceColumnId, cards
                });

                try {
                    await rootGetters.fetchPut({
                        path: `Lego/MoveCards/${cardId}/column/${targetColumnId}?newIndex=${newIndex}`
                    });
                } catch (e) {
                    commit("setCardsByColumnId", {
                        columnId: sourceColumnId, cards: prevSourceCards
                    });
                    await Swal.fire({
                        icon: "error", title: "Error!", text: "No se pudo mover la tarjeta: " + e.message
                    });
                }
            }
            // Movimiento entre columnas
            else {
                const sourceCards = [...prevSourceCards];
                const targetCards = [...prevTargetCards];

                const cardIndex = sourceCards.findIndex(c => c.id === cardId);
                if (cardIndex === -1) return;

                const [movedCard] = sourceCards.splice(cardIndex, 1);
                targetCards.splice(newIndex, 0, movedCard);

                commit("setCardsByColumnId", {
                    columnId: sourceColumnId, cards: sourceCards
                });
                commit("setCardsByColumnId", {
                    columnId: targetColumnId, cards: targetCards
                });

                try {
                    await rootGetters.fetchPut({
                        path: `Lego/MoveCards/${cardId}/column/${targetColumnId}?newIndex=${newIndex}`
                    });
                } catch (e) {
                    // Revertir cambios en caso de error
                    commit("setCardsByColumnId", {
                        columnId: sourceColumnId, cards: prevSourceCards
                    });
                    commit("setCardsByColumnId", {
                        columnId: targetColumnId, cards: prevTargetCards
                    });
                    await Swal.fire({
                        icon: "error", title: "Error!", text: "No se pudo mover la tarjeta"
                    });
                }
            }
        },**/

    async moveCardv3(
      { state, rootGetters, commit },
      { cardId, sourceColumnId, targetColumnId, newIndex }
    ) {
      const sourceColumn = state.board.paginatedColumns.items.find(
        (c) => c.id === sourceColumnId
      );
      const targetColumn = state.board.paginatedColumns.items.find(
        (c) => c.id === targetColumnId
      );
      if (!sourceColumn || !targetColumn) return;

      // Guardar copias antes del cambio (VueDraggable ya cambió las listas)
      const prevSourceCards = [...sourceColumn.paginatedCards.items];
      const prevTargetCards =
        sourceColumnId === targetColumnId
          ? prevSourceCards
          : [...targetColumn.paginatedCards.items];

      if (sourceColumnId !== targetColumnId) {
        if (sourceColumn) sourceColumn.paginatedCards.total -= 1;
        if (targetColumn) targetColumn.paginatedCards.total += 1;
        let cardUpdate = targetColumn.paginatedCards.items.find(
          (x) => x.id === cardId
        );
        cardUpdate.legoColumnId = targetColumnId;

        // Actualizar el progreso de las columnas cuando se mueve una tarjeta finalizada
        if (cardUpdate && cardUpdate.isDone) {
          // Restar de la columna origen si la tarjeta está finalizada
          if (
            sourceColumn &&
            typeof sourceColumn.paginatedCards.completed === "number"
          ) {
            sourceColumn.paginatedCards.completed = Math.max(
              0,
              sourceColumn.paginatedCards.completed - 1
            );
          }
          // Sumar a la columna destino si la tarjeta está finalizada
          if (
            targetColumn &&
            typeof targetColumn.paginatedCards.completed === "number"
          ) {
            targetColumn.paginatedCards.completed += 1;
          }
        }
      }
      try {
        // Llamar al backend
        await rootGetters.fetchPut({
          path: `Lego/MoveCards/${cardId}/column/${targetColumnId}?newIndex=${newIndex}`,
        });
      } catch (e) {
        // Rollback visual si falló
        commit("setCardsByColumnId", {
          columnId: sourceColumnId,
          cards: prevSourceCards,
        });
        if (sourceColumnId !== targetColumnId) {
          commit("setCardsByColumnId", {
            columnId: targetColumnId,
            cards: prevTargetCards,
          });
        }

        await Swal.fire({
          icon: "error",
          title: "Error!",
          text: "No se pudo mover la tarjeta: " + e.message,
        });
      }
    },

    async deleteColumn({ rootGetters, commit }, column) {
      await Swal.fire({
        title: `¿Desea eliminar la columnas?`,
        text: `La columnas "${column.title}" será eliminado y no podrá recuperarse.`,
        icon: "warning",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await rootGetters.fetchDelete({
              path: `Lego/DeleteColumn/${column.id}`,
            });
            if (response.ok) {
              commit("deleteColumn", column);
            } else {
              throw new Error("No se pudo eliminar la columna");
            }
          } catch (error) {
            await Swal.fire({
              icon: "error",
              title: "Error!",
              text: error.message,
            });
          }
        }
      });
    },
    async deleteCardAndSubCard({ rootGetters, commit }, card) {
      await Swal.fire({
        title: `¿Desea eliminar esta tarjeta?`,
        text: `La tarjeta "${card.title}" será eliminada y no podrá recuperarse.`,
        icon: "warning",
        showCancelButton: true,
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await rootGetters.fetchDelete({
              path: `Lego/DeleteCard/${card.id}`,
            });
            if (response.ok) {
              commit("deleteCardAndSubCard", card.id);
            } else {
              throw new Error("No se pudo eliminar la tarjeta");
            }
          } catch (error) {
            Swal.fire({
              icon: "error",
              title: "Error!",
              text: error.message,
            });
          }
        }
      });
    },

    async deleteItemInChecklist({ rootGetters, commit, dispatch }, item) {
      try {
        dispatch("assertInfoRoleNotCreator", item.createdById);
        const response = await rootGetters.fetchDelete({
          path: `Lego/DeleteChecklistItem/${item.id}`,
        });
        if (response.ok) {
          commit("deleteItemInChecklist", item);
        } else {
          throw new Error("No se pudo eliminar el item");
        }
      } catch (error) {
        showErrorAlert(error);
        throw error; // Re-lanzar la excepción para que el componente la capture
      }
    },
    async deleteSubItemInChecklist({ rootGetters, commit, dispatch }, item) {
      try {
        dispatch("assertInfoRoleNotCreator", item.createdById);
        const response = await rootGetters.fetchDelete({
          path: `Lego/DeleteChecklistItem/${item.id}`,
        });
        if (response.ok) {
          commit("deleteSubItemInChecklist", item);
        } else {
          throw new Error("No se pudo eliminar el subitem");
        }
      } catch (error) {
        showErrorAlert(error);
        throw error; // Re-lanzar la excepción para que el componente la capture
      }
    },
    async deleteChecklist({ rootGetters, commit, dispatch }, checklist) {
      try {
        dispatch("assertInfoRoleNotCreator", checklist.createdById);
        const response = await rootGetters.fetchDelete({
          path: `Lego/DeleteChecklist/${checklist.id}`,
        });
        if (response.ok) {
          commit("deleteChecklist", checklist);
        } else {
          throw new Error("No se pudo eliminar el checklist");
        }
      } catch (error) {
        showErrorAlert(error);
        throw error; // Re-lanzar la excepción para que el componente la capture
      }
    },

    async deleteAssignment({ rootGetters, commit }, assignment) {
      try {
        const { deleteFromSubCards, ...assignmentData } = assignment;
        const queryParams = deleteFromSubCards
          ? `?deleteFromSubCards=${deleteFromSubCards}`
          : "";

        const response = await rootGetters.fetchDelete({
          path: `Lego/DeleteAssignment/${assignment.id}${queryParams}`,
        });
        console.log("response", response);
        if (response.ok) {
          commit("deleteAssignment", {
            assignment: assignmentData,
            deleteFromSubCards: deleteFromSubCards,
          });
        } else {
          throw new Error("No se pudo eliminar la asignación");
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: error.message,
        });
      }
    },

    async getAllRecourse({ rootGetters, commit }) {
      let response = await rootGetters.fetchGet({ path: `Lego/Auditoria` });
      let auditoriaOptions = await response.json();
      auditoriaOptions.unshift({ id: null, name: "No aplica" });
      commit("setAuditoriaOptions", auditoriaOptions);
    },
    // Acciones para el zoom con persistencia
    initializeZoom({ commit }) {
      try {
        const storedLevel = localStorage.getItem("tableroKanbanZoom");
        const storedPosition = localStorage.getItem(
          "tableroKanbanZoomPosition"
        );

        if (storedLevel) {
          const level = parseFloat(storedLevel);
          if (!isNaN(level)) {
            commit("setZoomLevel", level);
          }
        }

        if (storedPosition === "left" || storedPosition === "right") {
          commit("setZoomPosition", storedPosition);
        }
      } catch (error) {
        console.warn("Error al cargar zoom desde localStorage:", error);
      }
    },
    saveZoomToStorage({ state }) {
      try {
        localStorage.setItem("tableroKanbanZoom", state.zoom.level.toString());
        localStorage.setItem("tableroKanbanZoomPosition", state.zoom.position);
      } catch (error) {
        console.warn("Error al guardar zoom en localStorage:", error);
      }
    },
    zoomIn({ commit, dispatch }) {
      commit("incrementZoom");
      dispatch("saveZoomToStorage");
    },
    zoomOut({ commit, dispatch }) {
      commit("decrementZoom");
      dispatch("saveZoomToStorage");
    },
    setZoom({ commit, dispatch }, level) {
      commit("setZoomLevel", level);
      dispatch("saveZoomToStorage");
    },
    resetZoomLevel({ commit, dispatch }) {
      commit("resetZoom");
      dispatch("saveZoomToStorage");
    },
    toggleZoomPosition({ commit, dispatch }) {
      commit("toggleZoomPosition");
      dispatch("saveZoomToStorage");
    },
  },
  getters: {
    getTasksLoading: (state) => (columnId) => {
      console.log("getTasksLoading", state.tasksLoading[columnId]);

      return state.tasksLoading[columnId] || false; // Devuelve false si no hay estado de carga para la columna
    },
    getRoleInBoard() {
      return (userId, collaborators) => {
        try {
          const thisUser = collaborators.find((c) => c.personaId == userId);
          return thisUser.role;
        } catch (error) {
          return {
            id: "F838CBF3-16BF-402D-BCDE-638EDAF8E4D5",
            name: "Invitado",
            level: 4,
          };
        }
      };
    },
    // Getters para el zoom
    getZoomLevel: (state) => state.zoom.level,
    getZoomPosition: (state) => state.zoom.position,
    getZoomConfig: (state) => state.zoom,
    getZoomPercentage: (state) => Math.round(state.zoom.level * 100),
    // Getter para el estado de eliminación
    getIsDeletingBoard: (state) => state.isDeletingBoard,
  },
};

export default store;
